import React, { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Table } from '../components/ui/Table';
import { DatePicker } from '../components/ui/DatePicker';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { callBillingAPI, callWFAPI } from '../utils/api';
import { formatCurrency } from '../utils/formatters';
import { PlusIcon, SearchIcon, EyeIcon, EditIcon, AlertCircleIcon, FilterIcon, Share2Icon, ChevronLeftIcon, ChevronRightIcon, RefreshCwIcon, SendIcon } from 'lucide-react';
// Storage key for persisting invoice filters
export const INVOICE_FILTERS_STORAGE_KEY = 'invoiceFilters';
// Storage key for caching WF order filters - now per company
const WF_ORDER_FILTERS_CACHE_KEY_PREFIX = 'wfOrderFiltersCache_';
// Storage key for caching WF shipping methods - per company
const WF_SHIP_METHODS_CACHE_KEY_PREFIX = 'wfShipMethodsCache_';
// Cache interface
interface WFOrderFiltersCache {
  companyId: string;
  timestamp: number;
  statusFilters: Array<{
    id: string;
    name: string;
  }>;
}
// Cache interface for shipping methods
interface WFShipMethodsCache {
  companyId: string;
  timestamp: number;
  shipMethods: string[];
}
// API invoice type
interface ApiInvoice {
  id: string;
  number: string;
  status: string;
  customer_id: string;
  customer_name: string;
  customer_organization?: string;
  source?: string;
  amount: number;
  invoice_date?: number; // optional, unix timestamp
  hash: string; // hash for public URL (legacy)
  link?: string; // absolute link to public invoice
  wf_status_id?: string; // WF order status ID (moved from extra)
  extra?: {
    [key: string]: any;
  };
}
// Display invoice type
interface Invoice {
  id: string;
  number: string;
  status: string;
  customer_id: string;
  customer_name: string;
  customer_organization?: string;
  source?: string;
  amount: number;
  invoice_date: Date | null;
  hash: string; // hash for public URL (legacy)
  link: string | null; // absolute link to public invoice
  wf_status_id?: string; // WF order status ID (moved from extra)
  extra?: {
    [key: string]: any;
  };
}
export function Invoices() {
  const {
    selectedCompanyId,
    selectedCompany
  } = useData();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // Data state
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  // Get filter values from URL search params
  const statusFilter = searchParams.get('status') || 'all';
  const searchQuery = searchParams.get('search') || '';
  const startDate = searchParams.get('from') || '';
  const endDate = searchParams.get('to') || '';
  const wfStatusFilter = searchParams.get('wf_status') || 'all';
  const sourceFilter = searchParams.get('source') || 'all';
  const wfShipMethodFilter = searchParams.get('wf_ship_method') || 'all';
  const pageParam = searchParams.get('page');
  // Local state for search input (for debounce)
  const [searchInput, setSearchInput] = useState(searchQuery);
  const [showFilters, setShowFilters] = useState(!!startDate || !!endDate || wfStatusFilter !== 'all' || sourceFilter !== 'all' || wfShipMethodFilter !== 'all');
  // Share modal state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareInvoiceLink, setShareInvoiceLink] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  // Send invoice state
  const [sendingInvoiceId, setSendingInvoiceId] = useState<string | null>(null);
  // WF Order Filters state
  const [wfOrderFilters, setWfOrderFilters] = useState<Array<{
    id: string;
    name: string;
  }> | null>(null);
  // WF Shipping Methods state
  const [wfShipMethods, setWfShipMethods] = useState<string[] | null>(null);
  // Save current filters to sessionStorage whenever they change
  useEffect(() => {
    const currentFilters = searchParams.toString();
    if (currentFilters) {
      sessionStorage.setItem(INVOICE_FILTERS_STORAGE_KEY, currentFilters);
    } else {
      sessionStorage.removeItem(INVOICE_FILTERS_STORAGE_KEY);
    }
  }, [searchParams]);
  // Helper function to update URL search params
  const updateSearchParams = (updates: Record<string, string | null>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === '' || value === 'all') {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    // Reset page to 1 when filters change (except when explicitly setting page)
    if (!('page' in updates)) {
      newParams.delete('page');
    }
    setSearchParams(newParams);
  };
  // Apply filter from navigation state (from Dashboard clicks) - on mount only
  useEffect(() => {
    const state = location.state as {
      filterStatus?: string;
    } | null;
    if (state?.filterStatus) {
      // Directly set search params so the fetch effect picks up the filter
      const newParams = new URLSearchParams(searchParams);
      newParams.set('status', state.filterStatus);
      setSearchParams(newParams, {
        replace: true
      });
      window.history.replaceState({}, document.title);
    }
  }, []); // Run only on mount
  // Sync search input with URL param on mount
  useEffect(() => {
    setSearchInput(searchQuery);
  }, []);
  // Fetch WF order filters when company has WF integration enabled
  useEffect(() => {
    const fetchWFOrderFilters = async () => {
      if (!selectedCompany || !selectedCompanyId) {
        setWfOrderFilters(null);
        return;
      }
      // Check if company has WF orders enabled
      if (selectedCompany.use_wf_orders !== 1 || !selectedCompany.wf_api_info) {
        setWfOrderFilters(null);
        return;
      }
      // Use per-company cache key
      const cacheKey = `${WF_ORDER_FILTERS_CACHE_KEY_PREFIX}${selectedCompanyId}`;
      // Check cache first
      try {
        const cachedData = localStorage.getItem(cacheKey);
        if (cachedData) {
          const cache: WFOrderFiltersCache = JSON.parse(cachedData);
          console.log('Using cached WF order filters for company:', selectedCompanyId);
          setWfOrderFilters(cache.statusFilters);
          return;
        }
      } catch (e) {
        console.error('Failed to read WF cache:', e);
      }
      // Fetch from API if not cached
      try {
        console.log('Fetching WF order filters from API for company:', selectedCompanyId);
        const wfApiInfo = selectedCompany.wf_api_info;
        const wfFilters = await callWFAPI(wfApiInfo.login, wfApiInfo.key, wfApiInfo.ecomModuleId, 'getOrderFilters');
        const statusFilters = wfFilters.status_filters || [];
        console.log('WF order filters fetched:', statusFilters.length, 'statuses');
        setWfOrderFilters(statusFilters);
        // Cache the result with per-company key
        const cacheData: WFOrderFiltersCache = {
          companyId: selectedCompanyId,
          timestamp: Date.now(),
          statusFilters: statusFilters
        };
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
        console.log('WF order filters cached for company:', selectedCompanyId);
      } catch (err) {
        console.error('Failed to fetch WF order filters:', err);
      }
    };
    fetchWFOrderFilters();
  }, [selectedCompany, selectedCompanyId]);
  // Fetch WF shipping methods when company has WF integration enabled
  useEffect(() => {
    const fetchWFShipMethods = async () => {
      if (!selectedCompany || !selectedCompanyId) {
        setWfShipMethods(null);
        return;
      }
      if (selectedCompany.use_wf_orders !== 1 || !selectedCompany.wf_api_info) {
        setWfShipMethods(null);
        return;
      }
      const cacheKey = `${WF_SHIP_METHODS_CACHE_KEY_PREFIX}${selectedCompanyId}`;
      // Check cache first
      try {
        const cachedData = localStorage.getItem(cacheKey);
        if (cachedData) {
          const cache: WFShipMethodsCache = JSON.parse(cachedData);
          console.log('Using cached WF ship methods for company:', selectedCompanyId);
          setWfShipMethods(cache.shipMethods);
          return;
        }
      } catch (e) {
        console.error('Failed to read WF ship methods cache:', e);
      }
      // Fetch from API if not cached
      try {
        console.log('Fetching WF ship methods from API for company:', selectedCompanyId);
        const wfApiInfo = selectedCompany.wf_api_info;
        const result = await callWFAPI(wfApiInfo.login, wfApiInfo.key, wfApiInfo.ecomModuleId, 'getAllShippingMethods', {
          active: 1
        });
        // Extract unique names from the returned array of objects
        const allNames: string[] = Array.isArray(result) ? result.map((m: any) => m.name).filter(Boolean) : [];
        const uniqueNames = Array.from(new Set(allNames)).sort((a, b) => a.localeCompare(b));
        console.log('WF ship methods fetched:', uniqueNames.length, 'methods');
        setWfShipMethods(uniqueNames);
        // Cache the result
        const cacheData: WFShipMethodsCache = {
          companyId: selectedCompanyId,
          timestamp: Date.now(),
          shipMethods: uniqueNames
        };
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
        console.log('WF ship methods cached for company:', selectedCompanyId);
      } catch (err) {
        console.error('Failed to fetch WF ship methods:', err);
      }
    };
    fetchWFShipMethods();
  }, [selectedCompany, selectedCompanyId]);
  // Fetch invoices from API
  const fetchInvoices = async (page: number = 1, search: string = '', status: string = 'all', fromDate: string = '', toDate: string = '', wfStatus: string = 'all', source: string = 'all', wfShipMethod: string = 'all') => {
    if (!selectedCompanyId) return;
    setIsLoading(true);
    setError(null);
    try {
      const params: Record<string, any> = {
        company_id: selectedCompanyId,
        page: page
      };
      if (search.trim()) {
        params.search = search.trim();
      }
      if (status !== 'all') {
        params.status = status;
      }
      // Convert date from yyyy-mm-dd (input format) to mm-dd-yyyy (API format)
      if (fromDate) {
        const date = new Date(fromDate);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        params.from_date = `${month}-${day}-${year}`;
      }
      if (toDate) {
        const date = new Date(toDate);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        params.to_date = `${month}-${day}-${year}`;
      }
      // Add WF status filter if selected
      if (wfStatus !== 'all') {
        params.wf_status_id = wfStatus;
      }
      // Add WF shipping method filter if selected
      if (wfShipMethod !== 'all') {
        params.wf_ship_method = wfShipMethod;
      }
      // Add source filter if selected
      if (source === 'manual') {
        params.source = 'manual';
      } else if (source === 'web') {
        params.source = 'wf';
      }
      const response = await callBillingAPI('getInvoices', params);
      // Transform API invoices
      const apiInvoices: ApiInvoice[] = response.items || [];
      const transformedInvoices: Invoice[] = apiInvoices.map(inv => ({
        id: inv.id,
        number: inv.number,
        status: inv.status,
        customer_id: inv.customer_id,
        customer_name: inv.customer_name,
        customer_organization: inv.customer_organization || '',
        source: inv.source || '',
        amount: inv.amount,
        invoice_date: inv.invoice_date ? new Date(inv.invoice_date * 1000) : null,
        hash: inv.hash,
        link: inv.link || null,
        wf_status_id: inv.wf_status_id,
        extra: inv.extra
      }));
      setInvoices(transformedInvoices);
      setTotalCount(response.total_count || 0);
      setTotalPages(response.total_pages || 0);
      setHasNextPage(response.has_next_page || false);
      setCurrentPage(page);
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
      setError(err instanceof Error ? err.message : 'Failed to load invoices');
      setInvoices([]);
    } finally {
      setIsLoading(false);
    }
  };
  // Fetch on mount and when company/filters change (from URL params)
  useEffect(() => {
    const page = pageParam ? parseInt(pageParam) : 1;
    fetchInvoices(page, searchQuery, statusFilter, startDate, endDate, wfStatusFilter, sourceFilter, wfShipMethodFilter);
  }, [selectedCompanyId, statusFilter, startDate, endDate, searchQuery, wfStatusFilter, sourceFilter, wfShipMethodFilter, pageParam]);
  // Handle search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput !== searchQuery) {
        updateSearchParams({
          search: searchInput || null
        });
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);
  const handleStatusChange = (newStatus: string) => {
    updateSearchParams({
      status: newStatus
    });
  };
  const handleStartDateChange = (newDate: string) => {
    updateSearchParams({
      from: newDate || null
    });
    if (newDate) setShowFilters(true);
  };
  const handleEndDateChange = (newDate: string) => {
    updateSearchParams({
      to: newDate || null
    });
    if (newDate) setShowFilters(true);
  };
  const handleWfStatusChange = (newStatus: string) => {
    updateSearchParams({
      wf_status: newStatus
    });
    if (newStatus !== 'all') setShowFilters(true);
  };
  const handleWfShipMethodChange = (newMethod: string) => {
    updateSearchParams({
      wf_ship_method: newMethod
    });
    if (newMethod !== 'all') setShowFilters(true);
  };
  const handleSourceChange = (newSource: string) => {
    updateSearchParams({
      source: newSource
    });
    if (newSource !== 'all') setShowFilters(true);
  };
  const handleNextPage = () => {
    if (hasNextPage) {
      updateSearchParams({
        page: String(currentPage + 1)
      });
    }
  };
  const handlePrevPage = () => {
    if (currentPage > 1) {
      updateSearchParams({
        page: currentPage > 2 ? String(currentPage - 1) : null
      });
    }
  };
  const handleRefresh = () => {
    const page = pageParam ? parseInt(pageParam) : 1;
    fetchInvoices(page, searchQuery, statusFilter, startDate, endDate, wfStatusFilter, sourceFilter, wfShipMethodFilter);
  };
  const handleClearFilters = () => {
    setSearchInput('');
    setSearchParams(new URLSearchParams());
  };
  const handleShareInvoice = (invoice: Invoice) => {
    // Use the link field from API, fallback to constructing from hash
    const shareUrl = invoice.link || `${window.location.origin}/public/invoice/${invoice.hash}`;
    setShareInvoiceLink(shareUrl);
    setShareModalOpen(true);
    setCopySuccess(false);
  };
  const handleCopyLink = async () => {
    if (!shareInvoiceLink) return;
    try {
      await navigator.clipboard.writeText(shareInvoiceLink);
      setCopySuccess(true);
      setTimeout(() => {
        setShareModalOpen(false);
        setCopySuccess(false);
      }, 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
      alert('Please copy this link manually: ' + shareInvoiceLink);
    }
  };
  const handleViewLink = () => {
    if (!shareInvoiceLink) return;
    window.open(shareInvoiceLink, '_blank');
    setShareModalOpen(false);
  };
  // Handle send invoice
  const handleSendInvoice = async (invoice: Invoice) => {
    setSendingInvoiceId(invoice.id);
    try {
      await callBillingAPI('sendInvoice', {
        id: invoice.id
      });
      // Refresh the list after sending
      const page = pageParam ? parseInt(pageParam) : 1;
      await fetchInvoices(page, searchQuery, statusFilter, startDate, endDate, wfStatusFilter, sourceFilter, wfShipMethodFilter);
    } catch (err) {
      console.error('Failed to send invoice:', err);
      alert(err instanceof Error ? err.message : 'Failed to send invoice');
    } finally {
      setSendingInvoiceId(null);
    }
  };
  // Get status badge
  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, {
      variant: 'success' | 'warning' | 'danger' | 'info' | 'default';
      label: string;
    }> = {
      draft: {
        variant: 'default',
        label: 'Draft'
      },
      sent: {
        variant: 'info',
        label: 'Sent'
      },
      paid: {
        variant: 'success',
        label: 'Paid'
      },
      partial_paid: {
        variant: 'warning',
        label: 'Partial'
      },
      overdue: {
        variant: 'danger',
        label: 'Overdue'
      },
      voided: {
        variant: 'default',
        label: 'Voided'
      }
    };
    const config = statusConfig[status] || {
      variant: 'default' as const,
      label: status
    };
    return <Badge variant={config.variant} data-id="element-1595">{config.label}</Badge>;
  };
  // Format date
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const statusOptions = [{
    value: 'all',
    label: 'All Statuses'
  }, {
    value: 'draft',
    label: 'Draft'
  }, {
    value: 'sent',
    label: 'Sent'
  }, {
    value: 'paid',
    label: 'Paid'
  }, {
    value: 'partial_paid',
    label: 'Partial Paid'
  }, {
    value: 'overdue',
    label: 'Overdue'
  }, {
    value: 'voided',
    label: 'Voided'
  }];
  // Check if any filters are active
  const hasActiveFilters = statusFilter !== 'all' || searchQuery || startDate || endDate || wfStatusFilter !== 'all' || sourceFilter !== 'all' || wfShipMethodFilter !== 'all';
  // Helper function to get WF status name from cached filters
  const getWFStatusName = (wfStatusId: string): string | null => {
    if (!wfOrderFilters) {
      return null;
    }
    // Try both string comparison and loose comparison in case of type mismatch
    const status = wfOrderFilters.find(s => s.id === wfStatusId || String(s.id) === String(wfStatusId));
    return status ? status.name : null;
  };
  // Helper function to filter and format WF order status options for dropdown
  const getFilteredWfStatusOptions = () => {
    if (!wfOrderFilters) return [];
    const filteredOptions = wfOrderFilters.filter(filter => {
      // Only show statuses with numeric IDs
      const numericId = parseInt(filter.id);
      return !isNaN(numericId);
    }).map(filter => {
      const numericId = parseInt(filter.id);
      // If ID is zero or negative, enclose label with < and >
      const label = numericId <= 0 ? `<${filter.name}>` : filter.name;
      return {
        value: filter.id,
        label: label
      };
    });
    // Add "All Statuses" option at the beginning
    return [{
      value: 'all',
      label: 'All WF Statuses'
    }, ...filteredOptions];
  };
  // Source filter options
  const sourceOptions = [{
    value: 'all',
    label: 'All'
  }, {
    value: 'manual',
    label: 'Manual'
  }, {
    value: 'web',
    label: 'Web'
  }];
  const columns = [{
    key: 'number',
    header: 'Invoice',
    render: (invoice: Invoice) => {
      const isEditable = invoice.status === 'draft';
      const isWFInvoice = invoice.source === 'wf';
      const hasWFStatus = isWFInvoice && invoice.wf_status_id !== undefined && invoice.wf_status_id !== null;
      const wfStatusName = hasWFStatus ? getWFStatusName(invoice.wf_status_id!) : null;
      return <div data-id="element-1596">
            <Link to={isEditable ? `/invoices/${invoice.id}/edit` : `/invoices/${invoice.id}`} className="text-blue-600 hover:text-blue-800 font-medium" data-id="element-1597">
              {invoice.number || invoice.id.slice(0, 8)}
            </Link>
            {invoice.source && <p className="text-xs text-gray-500" data-id="element-1598">
                {invoice.source}
                {wfStatusName && <span className="ml-1" data-id="element-1599">• {wfStatusName}</span>}
              </p>}
          </div>;
    }
  }, {
    key: 'customer',
    header: 'Customer',
    render: (invoice: Invoice) => {
      const showOrg = invoice.customer_organization && invoice.customer_organization.trim() !== (invoice.customer_name || '').trim();
      return <div data-id="element-1600">
            <span className="font-medium" data-id="element-1601">
              {invoice.customer_name || 'Unknown'}
            </span>
            {showOrg && <p className="text-xs text-gray-500" data-id="element-1602">
                {invoice.customer_organization}
              </p>}
          </div>;
    }
  }, {
    key: 'invoice_date',
    header: 'Invoice Date',
    render: (invoice: Invoice) => <span className={invoice.invoice_date ? '' : 'text-gray-400'} data-id="element-1603">
          {invoice.invoice_date ? formatDate(invoice.invoice_date) : '—'}
        </span>
  }, {
    key: 'amount',
    header: 'Amount',
    render: (invoice: Invoice) => <span className="font-semibold" data-id="element-1604">{formatCurrency(invoice.amount)}</span>
  }, {
    key: 'status',
    header: 'Status',
    render: (invoice: Invoice) => getStatusBadge(invoice.status)
  }, {
    key: 'actions',
    header: '',
    render: (invoice: Invoice) => {
      const isEditable = invoice.status === 'draft';
      const isDraft = invoice.status === 'draft';
      const isSending = sendingInvoiceId === invoice.id;
      return <div className="flex items-center justify-end gap-1" data-id="element-1605">
            {isDraft && <Button variant="ghost" size="sm" onClick={e => {
          e.stopPropagation();
          handleSendInvoice(invoice);
        }} disabled={isSending} className="text-gray-600 hover:text-green-600 hover:bg-green-50" aria-label="Send invoice" title="Send invoice" data-id="element-1606">
                {isSending ? <RefreshCwIcon className="w-4 h-4 animate-spin" data-id="element-1607" /> : <SendIcon className="w-4 h-4" data-id="element-1608" />}
              </Button>}
            <Button variant="ghost" size="sm" onClick={e => {
          e.stopPropagation();
          handleShareInvoice(invoice);
        }} className="text-gray-600 hover:text-blue-600 hover:bg-blue-50" aria-label="Share public link" title="Share public view" data-id="element-1609">
              <Share2Icon className="w-4 h-4" data-id="element-1610" />
            </Button>
            <Link to={isEditable ? `/invoices/${invoice.id}/edit` : `/invoices/${invoice.id}`} data-id="element-1611">
              <Button variant="ghost" size="sm" aria-label={isEditable ? 'Edit invoice' : 'View invoice'} data-id="element-1612">
                {isEditable ? <EditIcon className="w-4 h-4" data-id="element-1613" /> : <EyeIcon className="w-4 h-4" data-id="element-1614" />}
              </Button>
            </Link>
          </div>;
    }
  }];
  return <div className="space-y-6" data-id="element-1615">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-1616">
        <div data-id="element-1617">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1618">Invoices</h1>
          <p className="text-gray-500 mt-1" data-id="element-1619">
            {totalCount} invoice{totalCount !== 1 ? 's' : ''} total
          </p>
        </div>
        <div className="flex gap-2" data-id="element-1620">
          <Button variant="secondary" onClick={handleRefresh} disabled={isLoading} leftIcon={<RefreshCwIcon className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} data-id="element-1622" />} data-id="element-1621">
            Refresh
          </Button>
          <Link to="/invoices/new" data-id="element-1623">
            <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1625" />} data-id="element-1624">
              New Invoice
            </Button>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <Card data-id="element-1626">
        <div className="space-y-4" data-id="element-1627">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-1628">
            <div className="flex-1 relative" data-id="element-1629">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1630" />
              <Input placeholder="Invoice number or customer" value={searchInput} onChange={e => setSearchInput(e.target.value)} className="pl-10" data-id="element-1631" />
            </div>
            <div className="w-full sm:w-48" data-id="element-1632">
              <Select options={statusOptions} value={statusFilter} onChange={e => handleStatusChange(e.target.value)} data-id="element-1633" />
            </div>
            <Button variant="secondary" onClick={() => setShowFilters(!showFilters)} leftIcon={<FilterIcon className="w-4 h-4" data-id="element-1635" />} data-id="element-1634">
              {showFilters ? 'Hide Filters' : 'More Filters'}
            </Button>
          </div>

          {showFilters && <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-gray-200" data-id="element-1636">
              <div className="w-full sm:w-48" data-id="element-1637">
                <DatePicker label="From Date" value={startDate} onChange={e => handleStartDateChange(e.target.value)} data-id="element-1638" />
              </div>
              <div className="w-full sm:w-48" data-id="element-1639">
                <DatePicker label="To Date" value={endDate} onChange={e => handleEndDateChange(e.target.value)} data-id="element-1640" />
              </div>
              {selectedCompany?.use_wf_orders === 1 && <>
                  <div className="w-full sm:w-48" data-id="element-1641">
                    <Select label="Source" options={sourceOptions} value={sourceFilter} onChange={e => handleSourceChange(e.target.value)} data-id="element-1642" />
                  </div>
                  {wfOrderFilters && wfOrderFilters.length > 0 && <div className="w-full sm:w-48" data-id="element-1643">
                      <Select label="WF Order Status" options={getFilteredWfStatusOptions()} value={wfStatusFilter} onChange={e => handleWfStatusChange(e.target.value)} data-id="element-1644" />
                    </div>}
                  {wfShipMethods && wfShipMethods.length > 0 && <div className="w-full sm:w-48" data-id="element-1645">
                      <Select label="Shipping Method" options={[{
                value: 'all',
                label: '<All Shipping Methods>'
              }, ...wfShipMethods.map(m => ({
                value: m,
                label: m
              }))]} value={wfShipMethodFilter} onChange={e => handleWfShipMethodChange(e.target.value)} data-id="element-1646" />
                    </div>}
                </>}
            </div>}

          {hasActiveFilters && <div data-id="element-1647">
              <Button variant="ghost" onClick={handleClearFilters} data-id="element-1648">
                Clear Filters
              </Button>
            </div>}
        </div>
      </Card>

      {/* Content */}
      {isLoading ? <Card data-id="element-1649">
          <div className="p-12 text-center" data-id="element-1650">
            <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" data-id="element-1651"></div>
            <p className="mt-4 text-sm text-gray-500" data-id="element-1652">Loading invoices...</p>
          </div>
        </Card> : error ? <Card data-id="element-1653">
          <div className="p-12 text-center" data-id="element-1654">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mb-4" data-id="element-1655">
              <AlertCircleIcon className="w-6 h-6 text-red-600" data-id="element-1656" />
            </div>
            <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-1657">
              Failed to load invoices
            </p>
            <p className="text-sm text-gray-500 mb-4" data-id="element-1658">{error}</p>
            <Button size="sm" onClick={handleRefresh} data-id="element-1659">
              Retry
            </Button>
          </div>
        </Card> : <>
          <Table columns={columns} data={invoices} keyExtractor={inv => inv.id} emptyMessage="No invoices found" data-id="element-1660" />

          {/* Pagination */}
          {totalCount > 0 && <Card data-id="element-1661">
              <div className="flex items-center justify-between" data-id="element-1662">
                <p className="text-sm text-gray-600" data-id="element-1663">
                  Page {currentPage} of {totalPages || 1}
                </p>
                <div className="flex gap-2" data-id="element-1664">
                  <Button variant="secondary" size="sm" onClick={handlePrevPage} disabled={currentPage === 1} leftIcon={<ChevronLeftIcon className="w-4 h-4" data-id="element-1666" />} data-id="element-1665">
                    Previous
                  </Button>
                  <Button variant="secondary" size="sm" onClick={handleNextPage} disabled={!hasNextPage} rightIcon={<ChevronRightIcon className="w-4 h-4" data-id="element-1668" />} data-id="element-1667">
                    Next
                  </Button>
                </div>
              </div>
            </Card>}
        </>}

      {/* Share Modal */}
      <Modal isOpen={shareModalOpen} onClose={() => setShareModalOpen(false)} title="Share Invoice" data-id="element-1669">
        <div className="space-y-4" data-id="element-1670">
          <p className="text-sm text-gray-600" data-id="element-1671">
            Share this invoice with your customer using the link below:
          </p>

          {shareInvoiceLink && <div className="p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-1672">
              <code className="text-xs text-gray-700 break-all" data-id="element-1673">
                {shareInvoiceLink}
              </code>
            </div>}

          <div className="flex gap-3 pt-2" data-id="element-1674">
            <Button variant="primary" onClick={handleCopyLink} className="flex-1" data-id="element-1675">
              {copySuccess ? <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-1676">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-1677" />
                  </svg>
                  Copied!
                </> : <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-1678">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" data-id="element-1679" />
                  </svg>
                  Copy Link
                </>}
            </Button>

            <Button variant="secondary" onClick={handleViewLink} className="flex-1" data-id="element-1680">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-1681">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" data-id="element-1682" />
              </svg>
              View
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}